import styles from 'css/src/components/app/recorder/CommonRecordingPresentation.module.scss';

import React, { useCallback, useState } from 'react';

import useResizeObserver from 'js/src/libs/useResizeObserver';

// The preview is always recorded/played back at 16:9, regardless of how much
// width or height the modal actually has available.
const STAGE_ASPECT_RATIO = 16 / 9;

type Props = {
    callout?: React.ReactNode;
    topLeft?: React.ReactNode;
    topCenter?: React.ReactNode;
    topRight?: React.ReactNode;
    centerStage?: React.ReactNode;
    deviceControls?: React.ReactNode;
    bottomCenter?: React.ReactNode;
    bottomRight?: React.ReactNode;
    className?: string;
};

const CommonRecordingPresentation = React.memo(function CommonRecordingPresentation({
    callout,
    topLeft,
    topCenter,
    topRight,
    centerStage,
    deviceControls,
    bottomCenter,
    bottomRight,
    className,
}: Props) {
    const showTopRow = topLeft || topCenter || topRight;
    const showDeviceControls = !!deviceControls;
    const showBottomRow = bottomCenter || bottomRight;

    // Bootstrap's `ratio` class derives height purely from width, so it can't
    // shrink when the modal is short on vertical space — it just overflows the
    // modal body and forces a scrollbar. Measure the space actually available
    // and fit the 16:9 stage inside it in both dimensions, like `object-fit:
    // contain`.
    const [stageSize, setStageSize] = useState<{ width: number; height: number } | null>(null);
    const handleStageWrapperResize = useCallback((target: Element) => {
        const { width, height } = target.getBoundingClientRect();
        if (width <= 0 || height <= 0) {
            return;
        }
        if (width / height > STAGE_ASPECT_RATIO) {
            setStageSize({ width: height * STAGE_ASPECT_RATIO, height });
        } else {
            setStageSize({ width, height: width / STAGE_ASPECT_RATIO });
        }
    }, []);
    const { resizeTargetRef: stageWrapperRef } = useResizeObserver<HTMLDivElement>({
        onResize: handleStageWrapperResize,
    });

    return (
        <div
            className={`container-fluid gx-0 d-flex flex-column gap-3 flex-grow-1 ${styles['common-recording-presentation']}${className ? ` ${className}` : ''}`}
        >
            {callout && (
                <div className="row">
                    <div className="col">{callout}</div>
                </div>
            )}
            {showTopRow && (
                <div className="row align-items-center">
                    <div className="col d-flex justify-content-start">{topLeft}</div>
                    <div className="col d-flex justify-content-center">{topCenter}</div>
                    <div className="col d-flex justify-content-end">{topRight}</div>
                </div>
            )}
            <div
                ref={stageWrapperRef}
                className={`flex-grow-1 d-flex justify-content-center align-items-center ${styles['video-stage-wrapper']}`}
            >
                <div
                    className={`bg-grey-200 ${styles['video-stage']}`}
                    style={
                        stageSize ? { width: stageSize.width, height: stageSize.height } : undefined
                    }
                >
                    {centerStage}
                </div>
            </div>
            {showDeviceControls && (
                <div className="row align-items-center">
                    <div className="col">{deviceControls}</div>
                </div>
            )}
            {showBottomRow && (
                <div className="row align-items-center">
                    <div className="col-1 d-flex justify-content-start"></div>
                    <div className="col d-flex justify-content-center gap-2">{bottomCenter}</div>
                    <div className="col-1 d-flex justify-content-end">{bottomRight}</div>
                </div>
            )}
        </div>
    );
});

export default CommonRecordingPresentation;
